import {
  Checkbox,
  HorizontalDivider,
  Icon,
  Input,
  Modal,
  Notification,
  Typography
} from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { MAX_FEATURE_NAME_LENGTH, MIN_LENGTH } from '@hero/constants';
import { formatCurrency, getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

const validationSchema = yup.object().shape({
  name: yup.string().min(MIN_LENGTH).max(MAX_FEATURE_NAME_LENGTH).required(),
  includeGifts: yup.boolean(),
  includeRecipients: yup.boolean(),
  includeExperience: yup.boolean()
});

function DuplicateModal({
  isOpen,
  onOpen,
  duplicateText,
  featureType,
  recipientCount,
  giftCount,
  giftPrice,
  contactType,
  giftMessage,
  digitalGiftCard,
  onSubmit
}) {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      name: t('copyText', { copyText: duplicateText }),
      includeGifts: true,
      includeRecipients: true,
      includeExperience: true
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit
  });

  const contactTypeI18nKey = {
    list: 'duplicateRecipientContactCheckboxHelpText',
    number: 'duplicateRecipientCountCheckboxHelpText',
    tags: 'duplicateRecipientContactCheckboxHelpText'
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onOpen(false)}
      title={t('duplicateFeature', { featureType })}
      primaryButtonProps={{
        children: t('createAndReview'),
        endIcon: <Icon name="next" />,
        onClick: formik.handleSubmit,
        disabled: !formik.isValid,
        loading: formik.isSubmitting
      }}
      secondaryButtonProps={{
        children: t('cancel'),
        onClick: () => onOpen(false)
      }}
      size="sm"
    >
      <S.Content>
        <Icon
          style={{ alignSelf: 'center' }}
          name={featureType === 'recognition' ? 'gift-box' : 'automation'}
          size="xxl"
        />

        <Input
          value={formik.values.name}
          onChange={e => formik.setFieldValue('name', e.target.value)}
          inputProps={{
            maxLength: MAX_FEATURE_NAME_LENGTH
          }}
          helperText={`${formik.values.name?.length ?? 0}/100 characters`}
          placeholder={featureType === 'recognition' ? t('giftName') : t('automationName')}
        />

        <S.CopyTextArea>
          <Typography variant="body2">{t('duplicateModalDescription')}:</Typography>
          <Notification
            status="warning"
            message={
              <Trans
                i18nKey="duplicateFeatureWarning"
                values={{
                  featureType
                }}
                components={{ b: <b /> }}
              />
            }
          />
        </S.CopyTextArea>
        <S.CheckboxArea>
          <S.CheckboxGroup>
            <Checkbox
              label={t('gifts')}
              checked={formik.values.includeGifts}
              onChange={e => formik.setFieldValue('includeGifts', e.target.checked)}
            />
            {giftCount && giftPrice && (
              <Typography style={{ marginLeft: 24 }} color="fg" emphasis="weak" variant="caption2">
                {t('duplicateGiftCheckboxHelpText', {
                  giftCount,
                  giftPrice: formatCurrency(giftPrice)
                })}
              </Typography>
            )}
          </S.CheckboxGroup>
          <HorizontalDivider />
          <S.CheckboxGroup>
            <Checkbox
              label={<Typography variant="body2">{t('recipients')}</Typography>}
              checked={formik.values.includeRecipients}
              onChange={e => formik.setFieldValue('includeRecipients', e.target.checked)}
            />
            {recipientCount && (
              <Typography style={{ marginLeft: 24 }} color="fg" emphasis="weak" variant="caption2">
                {t(contactTypeI18nKey[contactType], {
                  recipientCount
                })}
              </Typography>
            )}
          </S.CheckboxGroup>
          <HorizontalDivider />
          <S.CheckboxGroup>
            <Checkbox
              label={t('experience')}
              checked={formik.values.includeExperience}
              onChange={e => formik.setFieldValue('includeExperience', e.target.checked)}
            />
            {giftMessage && (
              <Typography
                style={{ marginLeft: 24 }}
                lineClamp={1}
                color="fg"
                emphasis="weak"
                variant="caption2"
                {...getSafeInnerHtml(giftMessage)}
              />
            )}
            {digitalGiftCard && (
              <Typography
                style={{ marginLeft: 24 }}
                lineClamp={1}
                color="fg"
                emphasis="weak"
                variant="caption2"
              >
                {t('amountDigitalCard', { amount: 1 })}
              </Typography>
            )}
          </S.CheckboxGroup>
        </S.CheckboxArea>
      </S.Content>
    </Modal>
  );
}

DuplicateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  duplicateText: PropTypes.string.isRequired,
  featureType: PropTypes.string.isRequired,
  recipientCount: PropTypes.number,
  giftCount: PropTypes.number,
  giftPrice: PropTypes.number,
  contactType: PropTypes.string.isRequired,
  giftMessage: PropTypes.string,
  digitalGiftCard: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired
};

export { DuplicateModal };
